import indexedDB from './indexedDB';
import api from './api';

class OfflinePaymentService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.setupOnlineListener();
    // Initialize IndexedDB on service creation
    this.ensureIndexedDBReady();
  }

  async ensureIndexedDBReady() {
    try {
      await indexedDB.init();
      console.log('IndexedDB initialized for offline payments');
    } catch (error) {
      console.error('Failed to initialize IndexedDB:', error);
    }
  }

  setupOnlineListener() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('Device is online, starting sync...');
      // Small delay to ensure network is stable
      setTimeout(() => {
        this.syncPendingPayments();
      }, 1000);
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('Device is offline');
    });

    // Also check periodically in case events don't fire
    setInterval(() => {
      const wasOnline = this.isOnline;
      this.isOnline = navigator.onLine;
      
      if (!wasOnline && this.isOnline) {
        console.log('Network status changed to online, starting sync...');
        setTimeout(() => {
          this.syncPendingPayments();
        }, 1000);
      }
    }, 5000);
  }

  async recordPayment(installmentId, paymentData) {
    const { paymentAmount, collectorId, notes } = paymentData;

    // Ensure IndexedDB is ready
    await this.ensureIndexedDBReady();

    // Generate unique payment ID
    const paymentId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const paymentRecord = {
      id: paymentId,
      installmentId: parseInt(installmentId),
      paymentAmount: parseFloat(paymentAmount),
      collectorId: parseInt(collectorId),
      notes: notes || '',
      timestamp: new Date().toISOString(),
      // Optional: GPS coordinates if available
      location: await this.getLocation(),
      status: 'PENDING',
      synced: false
    };

    // Update online status
    this.isOnline = navigator.onLine;

    if (this.isOnline) {
      try {
        console.log('Attempting to send payment online...');
        // Try to send to server first
        await api.post(`/collector/installments/${installmentId}/payment`, null, {
          params: {
            paymentAmount: paymentAmount,
            collectorId: collectorId,
            notes: notes,
          },
        });

        console.log('Payment sent successfully online');
        // If successful, mark as synced and save to IndexedDB for record
        paymentRecord.status = 'SYNCED';
        paymentRecord.synced = true;
        paymentRecord.syncedAt = new Date().toISOString();
        
        // Still save to IndexedDB for audit trail
        await indexedDB.savePendingPayment(paymentRecord);

        return {
          success: true,
          synced: true,
          payment: paymentRecord,
          message: 'Payment recorded successfully!'
        };
      } catch (error) {
        console.warn('Online payment failed, saving offline:', error);
        // Fall through to offline save
      }
    }

    // Save offline
    console.log('Saving payment offline...');
    try {
      const savedPayment = await indexedDB.savePendingPayment(paymentRecord);
      console.log('Payment saved offline successfully:', savedPayment.id);

      return {
        success: true,
        synced: false,
        payment: savedPayment,
        message: this.isOnline 
          ? 'Payment saved offline due to server error. Will sync when connection is restored.' 
          : 'Payment saved offline. Will sync when connection is restored.',
      };
    } catch (error) {
      console.error('Error saving payment offline:', error);
      return {
        success: false,
        synced: false,
        error: error.message,
        message: 'Failed to save payment. Please try again.',
      };
    }
  }

  async getLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        () => resolve(null),
        { timeout: 5000, maximumAge: 60000 }
      );
    });
  }

  async syncPendingPayments() {
    // Update online status
    this.isOnline = navigator.onLine;
    
    if (this.syncInProgress) {
      console.log('Sync already in progress, skipping...');
      return { success: false, message: 'Sync already in progress' };
    }

    if (!this.isOnline) {
      console.log('Device is offline, cannot sync');
      return { success: false, message: 'Device is offline. Cannot sync.' };
    }

    this.syncInProgress = true;
    console.log('Starting payment sync...');

    try {
      // Ensure IndexedDB is ready
      await this.ensureIndexedDBReady();
      
      const pendingPayments = await indexedDB.getPendingPayments();

      if (pendingPayments.length === 0) {
        console.log('No pending payments to sync');
        this.syncInProgress = false;
        return { success: true, synced: 0, message: 'No pending payments to sync' };
      }

      // IMPORTANT: Sort payments by timestamp to ensure chronological order
      // This is critical for partial payment logic (carryover) to work correctly
      // Payments must be processed in the order they were made
      const sortedPayments = [...pendingPayments].sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeA - timeB; // Oldest first (chronological order)
      });

      console.log(`Syncing ${sortedPayments.length} pending payments in chronological order...`);

      // Send all pending payments to backend in chronological order
      const syncResult = await api.post('/collector/payments/bulk-sync', {
        payments: sortedPayments.map(p => ({
          installmentId: p.installmentId,
          paymentAmount: p.paymentAmount,
          collectorId: p.collectorId,
          notes: p.notes || '',
          timestamp: p.timestamp,
          location: p.location || null,
          offlineId: p.id, // Include offline ID to prevent duplicates
        })),
      });

      console.log('Sync response:', syncResult.data);

      // Mark only successfully synced payments as synced
      const syncResults = syncResult.data.results || [];
      const syncedPaymentIds = new Set();
      const failedPaymentIds = new Set();
      
      // Match results with payments by installmentId and offlineId
      syncResults.forEach((result) => {
        // Find the corresponding payment by matching installmentId and offlineId
        const matchingPayment = sortedPayments.find(p => 
          p.installmentId === result.installmentId && 
          (result.offlineId ? p.id === result.offlineId : true)
        );
        
        if (matchingPayment) {
          if (result.success) {
            syncedPaymentIds.add(matchingPayment.id);
          } else {
            failedPaymentIds.add(matchingPayment.id);
            console.error(`Failed to sync payment ${matchingPayment.id} for installment ${result.installmentId}:`, result.error);
          }
        }
      });

      // Mark only successfully synced payments
      if (syncedPaymentIds.size > 0) {
        const syncPromises = Array.from(syncedPaymentIds).map(paymentId => 
          indexedDB.markPaymentAsSynced(paymentId)
        );
        await Promise.all(syncPromises);
      }
      
      console.log(`Marked ${syncedPaymentIds.size} payments as synced. ${failedPaymentIds.size} failed.`);
      
      // If some payments failed, log them for debugging
      if (failedPaymentIds.size > 0) {
        console.warn('Some payments failed to sync:', Array.from(failedPaymentIds));
      }

      // Dispatch event to notify components that payments have been synced
      window.dispatchEvent(new CustomEvent('offlinePaymentSynced', {
        detail: {
          syncedCount: syncedPaymentIds.size,
          failedCount: failedPaymentIds.size,
          syncedInstallmentIds: Array.from(syncedPaymentIds).map(id => {
            const payment = sortedPayments.find(p => p.id === id);
            return payment ? payment.installmentId : null;
          }).filter(id => id !== null)
        }
      }));

      // Optionally delete synced payments after some time
      // For now, we keep them for audit trail

      this.syncInProgress = false;

      return {
        success: true,
        synced: syncedPaymentIds.size,
        failed: failedPaymentIds.size,
        message: failedPaymentIds.size > 0
          ? `Synced ${syncedPaymentIds.size} payment(s). ${failedPaymentIds.size} failed.`
          : `Successfully synced ${syncedPaymentIds.size} payment(s)`,
      };
    } catch (error) {
      console.error('Sync failed:', error);
      this.syncInProgress = false;

      return {
        success: false,
        error: error.response?.data?.error || error.message,
        message: 'Failed to sync payments. Will retry when connection is stable.',
      };
    }
  }

  async getPendingPaymentsCount() {
    try {
      await this.ensureIndexedDBReady();
      const pending = await indexedDB.getPendingPayments();
      return pending.length;
    } catch (error) {
      console.error('Error getting pending payments count:', error);
      return 0;
    }
  }

  isDeviceOnline() {
    return this.isOnline;
  }

  // Manual sync trigger
  async manualSync() {
    // Update online status
    this.isOnline = navigator.onLine;
    
    if (!this.isOnline) {
      return {
        success: false,
        message: 'Device is offline. Cannot sync.',
      };
    }

    return await this.syncPendingPayments();
  }
}

export default new OfflinePaymentService();






